'use client'

import { Copy24, StatusActive24, X24 } from '@vinted/monochrome-icons'
import {
  Bubble,
  Button,
  Cell,
  Dialog,
  Divider,
  Icon,
  Label,
  Navigation,
  Spacer,
  Text,
  Tooltip,
} from '@vinted/web-ui'
import { useEffect, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  clickEvent,
  getTrackingJourneySummary,
  transformTrackingJourneySummaryResponse,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import MarkAsDeliveredToSenderConfirmationModal from '../MarkAsDeliveredToSenderConfirmationModal'
import MarkAsReceivedConfirmationModal from '../MarkAsReceivedConfirmationModal'
import Timeline from '../Timeline'

const SHOW_TOOLTIP_DURATION = 1000

type Props = {
  onClose: () => void
  isOpen: boolean
  transactionId?: number
  shipmentId?: number
}

const TrackingJourneySummaryModal = ({ onClose, isOpen, transactionId, shipmentId }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('conversation.tracking_journey_details')

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [isDeliveredToSenderConfirmationOpen, setIsDeliveredToSenderConfirmationOpen] =
    useState(false)
  const [isTooltipShown, setIsTooltipShown] = useState(false)

  const progressComponentAbTest = useAbTest('dd_tracking_screen_progress_component_v3')
  useTrackAbTest(progressComponentAbTest, isOpen)

  const {
    fetch: fetchTrackingData,
    transformedData: tracking,
    isLoading,
  } = useFetch(getTrackingJourneySummary, response =>
    transformTrackingJourneySummaryResponse(response),
  )

  useEffect(() => {
    if (!transactionId || !isOpen) return

    fetchTrackingData({ transactionId })
  }, [transactionId, isOpen, fetchTrackingData])

  const handleConfirmationOpen = () => setIsConfirmationOpen(true)
  const handleConfirmationClose = () => setIsConfirmationOpen(false)

  const handleDeliveredToSenderConfirmationOpen = () => setIsDeliveredToSenderConfirmationOpen(true)
  const handleDeliveredToSenderConfirmationClose = () =>
    setIsDeliveredToSenderConfirmationOpen(false)

  const closeModal = () => {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: 'close_page',
        screen: 'track_shipment_view',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
    onClose()
  }

  const openTooltip = () => {
    setIsTooltipShown(true)

    setTimeout(() => {
      setIsTooltipShown(false)
    }, SHOW_TOOLTIP_DURATION)
  }

  const trackCopyToClipboard = () => {
    const targetDetails = {
      transaction_id: transactionId,
      carrier_id: tracking?.currentCarrier?.code,
      carrier_priority: tracking?.currentCarrier?.priority,
      location: 'top',
    }

    track(
      clickEvent({
        target: 'copy_tracking_number',
        screen: 'track_shipment_view',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const copyToClipboard = (trackingNumber: string | undefined) => () => {
    trackCopyToClipboard()
    openTooltip()
    if (!trackingNumber) return

    navigator.clipboard.writeText(trackingNumber)
  }

  const trackOpenProvidersPageClick = () => {
    const targetDetails = {
      transaction_id: transactionId,
      carrier_id: tracking?.currentCarrier?.code,
      carrier_priority: tracking?.currentCarrier?.priority,
      location: 'top',
    }

    track(
      clickEvent({
        target: 'open_tracking_page',
        screen: 'track_shipment_view',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const renderTrackingDetails = () => {
    return (
      <Cell testId="tracking-journey-modal-tracking-details">
        <Bubble styling="tight">
          <Cell
            onClick={copyToClipboard(tracking?.currentCarrier?.trackingCode)}
            type="navigating"
            theme="transparent"
            body={
              <Cell
                styling="tight"
                theme="transparent"
                suffix={
                  <Icon
                    name={Copy24}
                    color="greyscale-level-2"
                    testId="tracking-journey-modal-copy-tracking-number"
                  />
                }
              >
                <div className="u-flexbox u-flex-wrap">
                  <Text as="h2" type="title" text={translate('tracking_number')} />
                  <Spacer orientation="vertical" size="small" />
                  <Tooltip
                    placement="bottom"
                    hover={false}
                    show={isTooltipShown}
                    content={translate('actions.copy_tracking_number')}
                  >
                    <div className="u-text-wrap">
                      <Text
                        as="h2"
                        type="title"
                        theme="muted"
                        text={tracking?.currentCarrier?.trackingCode}
                      />
                    </div>
                  </Tooltip>
                </div>
              </Cell>
            }
          />
          {tracking?.currentCarrier?.trackingUrl && (
            <>
              <Divider />
              <Cell
                onClick={trackOpenProvidersPageClick}
                type="navigating"
                theme="transparent"
                body={
                  <Text
                    as="h4"
                    type="caption"
                    text={translate('provider_details', {
                      carrier_name: tracking?.currentCarrier?.name,
                      'tracking-page-link': chunks => (
                        <a
                          key="tracking-page-link"
                          href={tracking?.currentCarrier?.trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {chunks}
                        </a>
                      ),
                    })}
                    testId="tracking-journey-modal-provider-details"
                  />
                }
              />
            </>
          )}
        </Bubble>
      </Cell>
    )
  }

  const renderEstimatedDelivery = () => {
    if (!tracking?.estimatedDetail?.status) return null

    return (
      <Cell
        testId="tracking-journey-modal-estimated-delivery"
        title={
          <>
            <Text as="h1" width="parent" type="heading" text={tracking?.estimatedDetail?.status} />
            <Text
              as="h1"
              width="parent"
              type="heading"
              text={tracking?.estimatedDetail?.timePeriod}
            />
          </>
        }
        body={
          tracking?.estimatedDetail?.message && (
            <>
              <Spacer />
              <Text
                as="h4"
                type="caption"
                theme="inherit"
                text={tracking.estimatedDetail.message}
              />
            </>
          )
        }
      />
    )
  }

  const renderMarkAsReceived = () => (
    <>
      <Divider />
      <Cell onClick={handleConfirmationOpen}>
        <Button
          styling="filled"
          text={translate('actions.confirm')}
          testId="tracking-journey-mark-as-received"
        />
      </Cell>
    </>
  )

  const renderMarkDeliveredToSenderReceived = () => (
    <>
      <Divider />
      <Cell onClick={handleDeliveredToSenderConfirmationOpen}>
        <Button styling="filled" text={translate('actions.confirm')} />
      </Cell>
    </>
  )

  const renderLoader = () => (
    <ContentLoader
      styling={ContentLoader.Styling.Wide}
      testId="tracking-journey-summary-modal-loader"
    />
  )

  const renderEmptyContent = () => {
    if (isLoading) return renderLoader()

    return (
      <>
        <Label type="leading" text={translate('label')} />
        <Cell prefix={<Icon name={StatusActive24} color="primary-default" />}>
          <Text as="span" text={translate('no_details')} theme="amplified" />
        </Cell>
      </>
    )
  }

  const renderContent = () => {
    if (isLoading) return renderLoader()

    return (
      <>
        {renderTrackingDetails()}
        {renderEstimatedDelivery()}
        <Label type="leading" text={translate('label')} />
        {tracking?.details && (
          <Timeline trackingDetails={tracking.details} transactionId={transactionId} />
        )}
      </>
    )
  }

  if (!transactionId || !shipmentId) return null

  return (
    <Dialog show={isOpen} closeOnOverlay enableCloseButton>
      <Navigation
        right={
          <Button
            styling="flat"
            onClick={closeModal}
            iconName={X24}
            inline
            testId="tracking-journey-close"
          />
        }
        body={translate('title')}
      />
      <Divider />
      <div className="modal-contained-content">
        {tracking?.details.length ? renderContent() : renderEmptyContent()}
      </div>
      {tracking?.canMarkAsReceived && renderMarkAsReceived()}
      {tracking?.canMarkAsReturnedToSender && renderMarkDeliveredToSenderReceived()}
      <MarkAsReceivedConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={handleConfirmationClose}
        transactionId={transactionId}
        shipmentId={shipmentId}
        screen="track_shipment_view"
        userSide={tracking?.userSide}
      />
      <MarkAsDeliveredToSenderConfirmationModal
        transactionId={transactionId}
        isOpen={isDeliveredToSenderConfirmationOpen}
        onClose={handleDeliveredToSenderConfirmationClose}
        screen="track_shipment_view"
      />
    </Dialog>
  )
}

export default TrackingJourneySummaryModal
